import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFileDifferenceApi } from 'infrastructure/api';
import { useDocumentStorageContext } from 'framework/documentStorage';
import useDocumentStorageService from 'domain/shared/documentStorage/documentStorageService';
import { normalizeStoragePath } from 'domain/shared/documentStorage';
import { loadDiffSafe, useDifferenceByLinks } from 'framework/DiffViewer';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData } from 'framework/form';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { COMPLIANCE_DOCUMENT_STATUS } from 'domain/shared/compliance';
import { COMPLIANCE_MD_EDITOR_PATH, COMPLIANCE_REGISTRY_PATH } from 'domain/compliance/complianceRegistry/router';
import {
  ComplianceDocumentVersionFinalizePageData,
  ComplianceDocumentVersionFinalizeShowData,
  DiffPageQueryParams,
} from './types';

const useComplianceDocumentVersionFinalizePageData = (): ComplianceDocumentVersionFinalizePageData => {
  const { search, state } = useLocation();
  const { getDocumentToken, getDocumentByPaths, uploadDocument, getValidationSchema } = useDocumentStorageContext();
  const { uploadArtifact } = useDocumentStorageService();
  const { getDifference } = useDifferenceByLinks(useFileDifferenceApi);
  const { addMessageAndRedirect } = useMessageService();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const queryParams: DiffPageQueryParams = {
    currentDocumentPath: params.get('currentDocumentPath') || '',
    previousDocumentPath: params.get('previousDocumentPath') || '',
    version: Number(params.get('version') || '0'),
    documentName: params.get('documentName') || '',
  };

  const editedContent = (state?.editedContent || '').trim();
  const backToEditorUrl = `${COMPLIANCE_MD_EDITOR_PATH}${search}`;

  const getDiffToken = async (path: string) => await getDocumentToken(normalizeStoragePath(path));

  const getShowData = async (): Promise<ComplianceDocumentVersionFinalizeShowData | void> => {
    try {
      const documents = await getDocumentByPaths([queryParams.currentDocumentPath]);
      const currentDocument = documents?.[0];

      const hasEditedContent = editedContent.length > 0;
      const newDocumentPath = hasEditedContent
        ? await uploadArtifact({
            file: new File([editedContent], 'docum-edited.md', { type: 'text/markdown;charset=utf-8' }),
          })
        : queryParams.currentDocumentPath;

      const hasComparableVersion = Boolean(queryParams.previousDocumentPath);
      const sourceDiffResult = hasComparableVersion
        ? await loadDiffSafe(getDiffToken, getDifference, queryParams.previousDocumentPath, newDocumentPath)
        : { diff: null, hasError: false };
      const currentMetadata = currentDocument.metadata || {};

      return {
        ...currentMetadata,
        isPolicySigned: currentDocument.isPolicySigned || false,
        paths: [newDocumentPath],
        status: currentMetadata.status || COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED,
        documentName: queryParams.documentName || currentMetadata.complianceName || '',
        hasComparableVersion,
        sourceDiff: sourceDiffResult.diff,
        loadErrors: {
          sourceDiff: sourceDiffResult.hasError,
        },
        metadata: {
          version: queryParams.version,
        },
      };
    } catch {
      addMessageAndRedirect(new ErrorMessage(DEFAULT_ERROR_MESSAGE), backToEditorUrl);
    }
  };

  const submitCreateVersion = async (data: FormFieldsData) => {
    const { version, metadata, sourceDiff, loadErrors, documentName, ...payload } = data;

    return uploadDocument(payload);
  };

  const afterSubmit = {
    redirect: COMPLIANCE_REGISTRY_PATH,
    message: 'You are awesome! The document version has been successfully saved.',
  };

  return {
    search,
    getShowData,
    submitCreateVersion,
    getValidationSchema,
    afterSubmit,
  };
};

export default useComplianceDocumentVersionFinalizePageData;
